import { pdkAxios } from '@/lib/pdk-axios.ts';
import { db } from '@/models/db';
import type {
  DeltaPacketRecord,
  FullHydrateResponse,
} from '@/models/hydrate.ts';
import { ProtectionPlanTermMiles } from '@/models/registry.ts';
import type {
  BaseStore,
  StoreHydrationDelegator,
} from '@/models/stores/base.ts';
import type { ProtectionPlanTermMiles as ProtectionPlanTermMilesType } from '@/models/types.ts';
import { makeAutoObservable, observable, type ObservableMap } from 'mobx';

class ProtectionPlanTermMilesStore
  implements BaseStore<ProtectionPlanTermMiles, ProtectionPlanTermMilesType>
{
  store: ObservableMap<string, ProtectionPlanTermMiles> = observable.map();

  constructor() {
    makeAutoObservable(this, { store: observable.shallow }, { autoBind: true });
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<ProtectionPlanTermMilesType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<ProtectionPlanTermMilesType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.protection_plan_term_miles.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.store.delete(delta.record_id);
      return;
    }

    this.upsert(delta.data);
  }

  get all() {
    return Array.from(this.store.values());
  }

  getById(id: string): ProtectionPlanTermMiles | undefined {
    return this.store.get(id);
  }

  add(input: ProtectionPlanTermMilesType) {
    const protectionPlanTermMiles = new ProtectionPlanTermMiles(input);
    this.store.set(protectionPlanTermMiles.id, protectionPlanTermMiles);
  }

  save(protectionPlanTermMiles: ProtectionPlanTermMiles) {
    this.store.delete(protectionPlanTermMiles.id);
    this.store.set(protectionPlanTermMiles.id, protectionPlanTermMiles);
  }

  upsert(protectionPlanTermMiles: ProtectionPlanTermMilesType) {
    const idToUse = protectionPlanTermMiles.id;
    const existingProtectionPlanTermMiles = this.getById(idToUse);
    if (existingProtectionPlanTermMiles) {
      this.replace({ id: idToUse, protectionPlanTermMiles });
    } else {
      this.add(protectionPlanTermMiles);
    }
  }

  replace({
    id,
    protectionPlanTermMiles,
  }: {
    id: string;
    protectionPlanTermMiles: ProtectionPlanTermMilesType;
  }) {
    const existingProtectionPlanTermMiles = this.getById(id);
    if (!existingProtectionPlanTermMiles) return;
    existingProtectionPlanTermMiles.term_miles =
      protectionPlanTermMiles.term_miles;

    this.save(existingProtectionPlanTermMiles);
  }
}

export const protectionPlanTermMilesStore = new ProtectionPlanTermMilesStore();

// TODO -- currently doing full hydrate every time, since protection plan term miles cant be tracked via conventional delta sync
class ProtectionPlanTermMilesStoreHydrationDelegator
  implements StoreHydrationDelegator<ProtectionPlanTermMilesType>
{
  async hydrate({
    fullHydrateEndpoint,
  }: {
    fullHydrateEndpoint: string;
    deltaSyncEndpoint: string | null;
  }) {
    const resp =
      await pdkAxios.get<FullHydrateResponse<ProtectionPlanTermMilesType>>(
        fullHydrateEndpoint,
      );
    for (const protectionPlanTermMiles of resp.data.data) {
      await db.protection_plan_term_miles.put(
        protectionPlanTermMiles,
        protectionPlanTermMiles.id,
      );
      protectionPlanTermMilesStore.upsert(protectionPlanTermMiles);
    }
  }
}

export const protectionPlanTermMilesStoreHydrationDelegator =
  new ProtectionPlanTermMilesStoreHydrationDelegator();
